import React, { useContext } from 'react'
import styled from 'styled-components'
import PaneBody from 'components/Common/Pane/PaneBody'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Modal from 'components/Luxkit/Modal/Modal'
import Spacing from 'components/Common/Spacing'
import List from 'components/Common/List'
import { TermsAndConditions } from 'components/Booking/Payment/Links'
import { connect } from 'react-redux'
import { getDepositServiceFeeConfig } from 'checkout/selectors/featureConfig/deposit'
import moment from 'moment'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { getFlexibleCancellationDays, isFlexibleCancellationPolicy } from 'lib/cancellationPolicy'
import { FOURTEEN_DAYS_BEFORE_CHECK_IN, SEVEN_DAYS_AFTER_PURCHASE } from 'constants/cancellationPolicies'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import Hr from 'components/Common/Hr'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import { Trans, useTranslation } from 'react-i18next'
import CurrencyContext from 'contexts/currencyContext'

const CancellationTermsWrapper = styled.div`
  display: grid;
  grid-column-gap: ${rem(18)};
  margin-top: ${rem(12)};

  ${mediaQueryUp.sm} {
    grid-template-rows: 1fr;
    grid-template-columns: 1fr;
  }
`

const TermsGrid = styled.div`
  display: grid;
  grid-column-gap: ${rem(16)};
  grid-row-gap: ${rem(18)};
  grid-template-columns: 1fr 2fr;

  ${mediaQueryUp.sm} {
    grid-template-columns: 1fr 1fr;
  }
`

interface Props {
  isPolicyOpen: boolean;
  closePolicy: () => void;
  depositPercentage: number | undefined;
  remainingBalance?: number;
  depositAmount?: number;
  cancellationPolicy?: App.OfferCancellationPolicy;
  propertyTimezoneOffset?: number;
  checkInDate?: string | moment.Moment;
  depositPaymentDueDaysLeft?: number;
}

interface MappedProps {
  serviceFee: number;
}

function PayInInstalmentsMoreInfoModal(props : Props & MappedProps) {
  const {
    isPolicyOpen,
    closePolicy,
    depositPercentage,
    remainingBalance,
    depositAmount,
    serviceFee,
    cancellationPolicy,
    propertyTimezoneOffset,
    checkInDate,
    depositPaymentDueDaysLeft,
  } = props

  const { t } = useTranslation(['common', 'deposit'])

  const days = cancellationPolicy ? getFlexibleCancellationDays(cancellationPolicy) : 21
  let startBasedOnToday = propertyTimezoneOffset ? moment().utcOffset(propertyTimezoneOffset).add(SEVEN_DAYS_AFTER_PURCHASE, 'days') : moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')
  let startBasedOnCheckIn = checkInDate && (propertyTimezoneOffset ? moment(checkInDate).utcOffset(propertyTimezoneOffset).subtract(days, 'days') : moment(checkInDate).subtract(days, 'days'))

  const changeOfMindDeadline = checkInDate && propertyTimezoneOffset ? moment(checkInDate).utcOffset(propertyTimezoneOffset).subtract(FOURTEEN_DAYS_BEFORE_CHECK_IN, 'days') : moment(checkInDate).subtract(FOURTEEN_DAYS_BEFORE_CHECK_IN, 'days')
  startBasedOnToday = startBasedOnToday.isBefore(changeOfMindDeadline) ? startBasedOnToday : changeOfMindDeadline

  const cancellationAfterChangeOfMindPeriod = checkInDate && startBasedOnToday.isBefore(changeOfMindDeadline) && moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days').isBefore(moment(checkInDate).subtract(days, 'days'))
  startBasedOnCheckIn = cancellationAfterChangeOfMindPeriod ? startBasedOnCheckIn : startBasedOnToday

  const currency = useContext(CurrencyContext)

  return <Modal title={t('howDoDepositsWork', { ns: 'deposit' })} isOpen={isPolicyOpen} onClose={closePolicy}>
    <PaneBody size="zero">
      <VerticalSpacer gap={24}>
        <BodyText variant="medium">
          {depositPaymentDueDaysLeft && t('secureSelectPackagesDaysPrior', { ns: 'deposit', depositPercentage, numberOfDays: depositPaymentDueDaysLeft })}
          {!depositPaymentDueDaysLeft && t('secureSelectPackages', { ns: 'deposit', depositPercentage })}
        </BodyText>

        <BodyText variant="medium">
          <VerticalSpacer gap={8}>
            <Heading variant="heading6">
              {t('Choose your preferred payment plan', { ns: 'deposit' })}
            </Heading>
            <div>{remainingBalance && t('enjoyTheFreedomRemainingBalance', { ns: 'deposit', remainingBalance, currency })}</div>
            <div>{!remainingBalance && t('enjoyTheFreedom', { ns: 'deposit' })}</div>
            <List>
              <li>{t('A single payment', { ns: 'deposit' })}</li>
              <li>{t('Weekly payments', { ns: 'deposit' })}</li>
              <li>{t('Fortnightly payments', { ns: 'deposit' })}</li>
              <li>{t('Monthly payments', { ns: 'deposit' })}</li>
            </List>
            <div>{t('eachPaymentAutomaticallyCharged', { ns: 'deposit' })}</div>
          </VerticalSpacer>
        </BodyText>

        <BodyText variant="medium">
          <Heading variant="heading6">
            {t('Service fee', { ns: 'deposit' })}
          </Heading>
          <Spacing mt={8} />
          {(remainingBalance && depositAmount) && t('serviceFeeMessageWithAmounts', { ns: 'deposit', serviceFee, depositAmount, remainingBalance, depositPercentage, currency })}
          {!(remainingBalance && depositAmount) && t('serviceFeeMessageWithoutAmounts', { ns: 'deposit', serviceFee, depositPercentage })}
        </BodyText>

        <BodyText variant="medium">
          <Heading variant="heading6">
            {t('Cancellation policy', { ns: 'common' })}
          </Heading>
          <Spacing mt={8} />
          {!cancellationPolicy &&
            t('cancelWithinFreeCancellationPolicyPeriod', { ns: 'deposit' })
          }

          {cancellationPolicy &&
            <>
              {t('Please see full cancellation breakdown below', { ns: 'deposit' })}
              <CancellationTermsWrapper>
                <TermsGrid>
                  {!checkInDate &&
                    <BodyText variant="medium" weight="semi-bold" as="span">
                      {t('Up to 7 days after purchase', { ns: 'deposit' })}
                    </BodyText>
                  }
                  {checkInDate &&
                    <BodyText variant="medium" weight="semi-bold" as="span">
                      {t('cancelUntil', { ns: 'deposit', date: startBasedOnToday, ...INTL_DMY_CASUAL_SHORT_FORMAT })}
                    </BodyText>
                  }
                  <BodyText variant="medium" as="span">
                    {t('100PercentRefundPaidToOriginal', { ns: 'deposit' })}
                  </BodyText>
                </TermsGrid>
                <Hr size="md"/>
                {isFlexibleCancellationPolicy(cancellationPolicy) &&
                  <>
                    {(cancellationAfterChangeOfMindPeriod && startBasedOnCheckIn) &&
                      <>
                        <TermsGrid>
                          <BodyText variant="medium" weight="semi-bold" as="span">
                            {t('cancelUntil', { ns: 'deposit', date: startBasedOnCheckIn, ...INTL_DMY_CASUAL_SHORT_FORMAT })}
                          </BodyText>
                          <BodyText variant="medium" as="span">
                            {t('100PercentRefundPaidToLECredit', { ns: 'deposit' })}
                          </BodyText>
                        </TermsGrid>
                        <Hr size="md"/>
                      </>
                    }
                    {(!cancellationAfterChangeOfMindPeriod && !checkInDate) &&
                      <>
                        <TermsGrid>
                          <BodyText variant="medium" weight="semi-bold" as="span">
                            {t('upToDaysPriorToCheckin', { ns: 'deposit', numberOfDays: days + 1 })}
                          </BodyText>
                          <BodyText variant="medium" as="span">
                            {t('100PercentRefundPaidToLECredit', { ns: 'deposit' })}
                          </BodyText>
                        </TermsGrid>
                        <Hr size="md"/>
                      </>
                    }
                  </>
                }
                <TermsGrid>
                  {!checkInDate && <BodyText variant="medium" weight="semi-bold" as="span">
                    {t('fromDaysUntilCheckin', { ns: 'common', days })}
                  </BodyText>}
                  {checkInDate && <BodyText variant="medium" weight="semi-bold" as="span">
                    {t('cancelAfter', { ns: 'deposit', date: startBasedOnCheckIn, ...INTL_DMY_CASUAL_SHORT_FORMAT })}
                  </BodyText>}
                  <BodyText variant="medium" as="span">
                    {t('Non-refundable', { ns: 'deposit', numberOfDays: days })}
                  </BodyText>
                </TermsGrid>
              </CancellationTermsWrapper>
            </>
          }
        </BodyText>

        <BodyText variant="medium">
          <Heading variant="heading6">
            {t('Failed payments', { ns: 'deposit' })}
          </Heading>
          <Spacing mt={8} />
          {t('failureResultInCancellation', { ns: 'deposit' })}
        </BodyText>

        <BodyText variant="medium">
          <Trans i18nKey="viewFullTermsAndConditionsLink" ns="deposit">
            View full <TermsAndConditions variant="black" />.
          </Trans>
        </BodyText>
      </VerticalSpacer>
    </PaneBody>
  </Modal>
}

const mapStateToProps = (state: App.State) => ({
  serviceFee: getDepositServiceFeeConfig(state).percentage,
})

export default connect(mapStateToProps)(PayInInstalmentsMoreInfoModal)
